import React, { useEffect, useState } from 'react';
import { inquiryService } from '../../services/inquiryService';
import { ArrowDownWideNarrow, ChevronDown, X, Copy } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import * as Dialog from '@radix-ui/react-dialog';
import ContactInquiries from './Inquiries/ContactInquiries';
import ValuationRequests from './Inquiries/ValuationRequest';
import PropertyInquiries from './Inquiries/PropertyInquiries';
import LoanRequests from './Inquiries/LoanRequests';

const InquiriesList = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [contactInquiries, setContactInquiries] = useState([]);
  const [valuationRequests, setValuationRequests] = useState([]);
  const [propertyInquiries, setPropertyInquiries] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const limit = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, [activeTab, page, statusFilter]);

  const fetchInquiries = async () => {
    try {
      setIsLoading(true);
      const options = {
        limit,
        offset: (page - 1) * limit,
        status: statusFilter === 'all' ? undefined : statusFilter,
      };

      if (activeTab === 'contact') {
        const result = await inquiryService.getAllContactInquiries(options);
        setContactInquiries(result.inquiries);
        setTotal(result.total);
      } else if (activeTab === 'valuation') {
        const result = await inquiryService.getAllValuationRequests(options);
        setValuationRequests(result.inquiries);
        setTotal(result.total);
      } else if (activeTab === 'property') {
        const result = await inquiryService.getAllPropertyInquiries(options);
        setPropertyInquiries(result.inquiries);
        setTotal(result.total);
      } else if (activeTab === 'loan') {
        const result = await inquiryService.getAllLoanRequests(options);
        setLoanRequests(result.inquiries);
        setTotal(result.total);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      if (activeTab === 'contact') {
        await inquiryService.updateContactInquiryStatus(id, status);
      } else if (activeTab === 'valuation') {
        await inquiryService.updateValuationRequestStatus(id, status);
      } else if (activeTab === 'property') {
        await inquiryService.updatePropertyInquiryStatus(id, status);
      } else if (activeTab === 'loan') {
        await inquiryService.updateLoanRequestStatus(id, status);
      }
      fetchInquiries();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const openMessageModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
    setCopyStatus('');
  };

  const closeMessageModal = () => {
    setIsModalOpen(false);
    setSelectedInquiry(null);
    setCopyStatus('');
  };

  const handleCopyDetails = () => {
    let detailsToCopy = '';
    if (selectedInquiry) {
      if (activeTab === 'contact') {
        detailsToCopy = `
Name: ${selectedInquiry.name}
Email: ${selectedInquiry.email}
${selectedInquiry.phone ? `Phone: ${selectedInquiry.phone}` : ''}
Message: ${selectedInquiry.message}
        `.trim();
      } else if (activeTab === 'valuation') {
        detailsToCopy = `
Name: ${selectedInquiry.name}
Email: ${selectedInquiry.email}
${selectedInquiry.phone ? `Phone: ${selectedInquiry.phone}` : ''}
Property Type: ${selectedInquiry.property_type}
Area: ${selectedInquiry.area} sq ft
Location: ${selectedInquiry.location}
        `.trim();
      } else if (activeTab === 'property') {
        detailsToCopy = `
Name: ${selectedInquiry.name}
Email: ${selectedInquiry.email}
${selectedInquiry.phone ? `Phone: ${selectedInquiry.phone}` : ''}
Property ID: ${selectedInquiry.property_id}
Property URL: ${selectedInquiry.property_url}
Message: ${selectedInquiry.message}
        `.trim();
      } else if (activeTab === 'loan') {
        detailsToCopy = `
Name: ${selectedInquiry.name}
Email: ${selectedInquiry.email}
${selectedInquiry.phone ? `Phone: ${selectedInquiry.phone}` : ''}
Loan Type: ${selectedInquiry.loan_type}
        `.trim();
      }
    }

    navigator.clipboard.writeText(detailsToCopy)
      .then(() => {
        setCopyStatus('Copied!');
        setTimeout(() => setCopyStatus(''), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        setCopyStatus('Failed to copy');
        setTimeout(() => setCopyStatus(''), 2000);
      });
  };

  const totalPages = Math.ceil(total / limit);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] bg-white dark:bg-gray-900 rounded-lg shadow-md p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-accent dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <h1 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-6">Inquiry Management</h1>

      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex flex-wrap justify-center sm:justify-start space-x-0 sm:space-x-6 px-4 pt-4">
            {['contact', 'valuation', 'property', 'loan'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setPage(1);
                  setStatusFilter('all');
                }}
                className={`
                  whitespace-nowrap py-3 px-4 border-b-2 font-semibold text-lg
                  ${activeTab === tab
                    ? 'border-accent text-primary-700'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                  transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                  mb-2 sm:mb-0
                `}
              >
                {tab === 'contact' ? 'Contact Inquiries' :
                  tab === 'valuation' ? 'Valuation Requests' :
                    tab === 'property' ? 'Property Inquiries' :
                      'Loan Requests'}
              </button>
            ))}
          </nav>
        </div>

        <div className="px-4 py-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-xl font-semibold dark:text-white text-gray-800">Inquiry List</h2>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <ArrowDownWideNarrow className="text-gray-600 dark:text-gray-400 w-5 h-5" />
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap sr-only">Filter by Status:</label>
            <Select.Root value={statusFilter} onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}>
              <Select.Trigger
                className="flex items-center justify-between w-full sm:w-auto rounded-full border-gray-300 dark:border-gray-700 shadow-sm
                           focus:border-primary-500 focus:ring-primary-500 text-base py-2 pl-3 pr-2
                           bg-white dark:bg-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-label="Filter by status"
              >
                <Select.Value />
                <Select.Icon className="ml-2">
                  <ChevronDown className="text-gray-800 dark:text-gray-200 w-4 h-4" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700"
                  position="popper"
                  sideOffset={5}
                >
                  <Select.Viewport>
                    <Select.Item
                      value="all"
                      className="text-base text-gray-700 dark:text-gray-300 py-2 px-4 cursor-pointer relative flex items-center
                                 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-700 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-white data-[highlighted]:outline-none"
                    >
                      <Select.ItemText>All Status</Select.ItemText>
                    </Select.Item>
                    <Select.Item
                      value="new"
                      className="text-base py-2 px-4 cursor-pointer relative flex items-center
                                 data-[highlighted]:bg-primary-100 data-[highlighted]:text-primary-800 data-[highlighted]:font-medium data-[highlighted]:outline-none
                                 bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                    >
                      <Select.ItemText>New</Select.ItemText>
                    </Select.Item>
                    <Select.Item
                      value="in-progress"
                      className="text-base py-2 px-4 cursor-pointer relative flex items-center
                                 data-[highlighted]:bg-primary-100 data-[highlighted]:text-primary-800 data-[highlighted]:font-medium data-[highlighted]:outline-none
                                 bg-yellow-50 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"
                    >
                      <Select.ItemText>In Progress</Select.ItemText>
                    </Select.Item>
                    <Select.Item
                      value="completed"
                      className="text-base py-2 px-4 cursor-pointer relative flex items-center
                                 data-[highlighted]:bg-primary-200 data-[highlighted]:text-primary-800 data-[highlighted]:font-medium data-[highlighted]:outline-none
                                 bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300"
                    >
                      <Select.ItemText>Completed</Select.ItemText>
                    </Select.Item>
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
        </div>

        <div className="overflow-x-auto md:overflow-x-visible">
          {activeTab === 'contact' && (
            <ContactInquiries
              inquiries={contactInquiries}
              handleStatusChange={handleStatusChange}
              openMessageModal={openMessageModal}
            />
          )}
          {activeTab === 'valuation' && (
            <ValuationRequests
              inquiries={valuationRequests}
              handleStatusChange={handleStatusChange}
              openMessageModal={openMessageModal}
            />
          )}
          {activeTab === 'property' && (
            <PropertyInquiries
              inquiries={propertyInquiries}
              handleStatusChange={handleStatusChange}
              openMessageModal={openMessageModal}
            />
          )}
          {activeTab === 'loan' && (
            <LoanRequests
              inquiries={loanRequests}
              handleStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300
                        hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed
                        transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Previous
          </button>
          <span className="text-md text-gray-700 dark:text-gray-300 font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300
                        hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed
                        transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Next
          </button>
        </div>
      )}

      <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[99] animate-overlayShow" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                                     w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto
                                     bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 md:p-8
                                     focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                                     z-[100] animate-contentShow
                                     flex flex-col">
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-full
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-label="Close"
                onClick={closeMessageModal}
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
            <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pr-10">
              Inquiry Details
            </Dialog.Title>
            {selectedInquiry && (
              <div className="text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-6 flex-grow space-y-3">
                {activeTab === 'contact' && (
                  <>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Name:</strong>{' '}
                      {selectedInquiry.name}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Email:</strong>{' '}
                      {selectedInquiry.email}
                    </p>
                    {selectedInquiry.phone && (
                      <p>
                        <strong className="font-semibold text-gray-800 dark:text-gray-200">Phone:</strong>{' '}
                        {selectedInquiry.phone}
                      </p>
                    )}
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Message:</strong>{' '}
                      {selectedInquiry.message}
                    </p>
                  </>
                )}
                {activeTab === 'valuation' && (
                  <>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Name:</strong>{' '}
                      {selectedInquiry.name}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Email:</strong>{' '}
                      {selectedInquiry.email}
                    </p>
                    {selectedInquiry.phone && (
                      <p>
                        <strong className="font-semibold text-gray-800 dark:text-gray-200">Phone:</strong>{' '}
                        {selectedInquiry.phone}
                      </p>
                    )}
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Property Type:</strong>{' '}
                      {selectedInquiry.property_type}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Area:</strong>{' '}
                      {selectedInquiry.area} sq ft
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Location:</strong>{' '}
                      {selectedInquiry.location}
                    </p>
                  </>
                )}
                {activeTab === 'property' && (
                  <>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Name:</strong>{' '}
                      {selectedInquiry.name}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Email:</strong>{' '}
                      {selectedInquiry.email}
                    </p>
                    {selectedInquiry.phone && (
                      <p>
                        <strong className="font-semibold text-gray-800 dark:text-gray-200">Phone:</strong>{' '}
                        {selectedInquiry.phone}
                      </p>
                    )}
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Property ID:</strong>{' '}
                      {selectedInquiry.property_id}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Property URL:</strong>{' '}
                      <a
                        href={selectedInquiry.property_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline break-all"
                      >
                        {selectedInquiry.property_url}
                      </a>
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Message:</strong>{' '}
                      {selectedInquiry.message}
                    </p>
                  </>
                )}
                {activeTab === 'loan' && (
                  <>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Name:</strong>{' '}
                      {selectedInquiry.name}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Email:</strong>{' '}
                      {selectedInquiry.email}
                    </p>
                    {selectedInquiry.phone && (
                      <p>
                        <strong className="font-semibold text-gray-800 dark:text-gray-200">Phone:</strong>{' '}
                        {selectedInquiry.phone}
                      </p>
                    )}
                    <p>
                      <strong className="font-semibold text-gray-800 dark:text-gray-200">Loan Type:</strong>{' '}
                      {selectedInquiry.loan_type}
                    </p>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-2">
              <div className="flex items-center">
                <button
                  onClick={handleCopyDetails}
                  className="flex items-center gap-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md font-semibold
                             hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                             transition-colors duration-200"
                >
                  <Copy className="w-4 h-4" /> Copy Details
                </button>
                {copyStatus && (
                  <span className="ml-3 text-sm text-green-600 animate-fade-in-out">
                    {copyStatus}
                  </span>
                )}
              </div>
              <Dialog.Close asChild>
                <button
                  className="px-6 py-2 bg-accent text-white rounded-md font-semibold
                             hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                             transition-colors duration-200"
                  onClick={closeMessageModal}
                >
                  Got It
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default InquiriesList;